import { useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import { request } from './graphql'
import { loadGlobals, withGlobals } from './globals'
import { withMetaTags } from './metaTags'

const availableLocales = null

export const getCraftData = ( fetchData, useParams = [] ) => async ctx => {
  const fetchDataFunc = getFetchFunc( fetchData, useParams )
  const previewProps = getPreviewProps( ctx )

  const fetchProps = {
    ...ctx,
    params: {
      ...ctx.params,
      ...ctx.query,
    },
    preview: previewProps,
  }

  const pageProps = await fetchDataFunc( fetchProps )
  const globals = await loadGlobals( fetchProps.params.locale )
  const acceptHeaders = ctx.req.headers.accept?.split( ',' ).map( h => h.trim() )

  if ( previewProps ) {
    ctx.res.setHeader( 'Cache-Control', 'private, no-store' )
  }
  else {
    ctx.res.setHeader( 'Cache-Control', 'public, max-age=60, s-maxage=300' )
  }

  return {
    props: {
      ...pageProps,
      globals,
      preview: previewProps,
      req: {
        supportsWebp: acceptHeaders?.includes( 'image/webp' ),
      },
    },
  }
}

export const withCraftData = ( PageComponent, fetchData, useParams = [] ) => {
  const fetchDataFunc = getFetchFunc( fetchData, useParams )
  const PageComponentWithMetaTags = withMetaTags( PageComponent )

  function WithCraftData( initPageProps ) {
    const [pageProps, setPageProps] = useState( initPageProps )
    const { query } = useRouter()

    // update pageProps state when initPageProps changes
    // so the page behaves as expected because we’re using
    // internal state
    useEffect( () => {
      setPageProps( initPageProps )
    }, [initPageProps] )

    // handle reloading data for Craft live previews
    useEffect( () => {
      const onPostMessage = event => {
        if ( event.data === 'CRAFT_CONTENT_UPDATED' ) {
          fetchDataFunc( {
            params: query,
            preview: pageProps.preview,
          } )
            .then( result => {
              setPageProps( {
                ...pageProps,
                ...result,
              } )
            } )
            .catch( e => console.error( e ) )
        }
      }

      if (
        typeof window !== 'undefined'
        && fetchDataFunc
        && pageProps.preview?.isLivePreview
      ) {
        window.addEventListener( 'message', onPostMessage )
      }

      return () => {
        window.removeEventListener( 'message', onPostMessage )
      }
    }, [] )

    // eslint-disable-next-line react/jsx-props-no-spreading
    return <PageComponentWithMetaTags { ...pageProps } />
  }

  return withGlobals( WithCraftData )
}

const getPreviewProps = ctx => {
  const { query } = ctx

  if (
    query?.token
    && (
      query[ 'x-craft-live-preview' ]
      || query[ 'x-craft-preview' ]
    )
  ) {
    return {
      previewToken: query?.token || null,
      isLivePreview: !!query[ 'x-craft-live-preview' ],
    }
  }

  return null
}

const getFetchFunc = ( fetchData, useParams ) => {
  const fetchFunc = typeof fetchData !== 'string'
    ? fetchData
    : async ( { params, preview } ) => {
      const variables = useParams.reduce(
        ( newObject, key ) => {
          // eslint-disable-next-line no-param-reassign
          newObject[ key ] = params[ key ] || params[ `${ key }[]` ]
          return newObject
        },
        {},
      )

      const response = await request(
        fetchData,
        { ...variables, site: params.site },
        preview,
      )

      return response.data
    }

  return async ( { params, preview } ) => {
    const locales = availableLocales?.map( l => l.code ).includes( params.locale )
      ? [params.locale]
      : ['en']

    return fetchFunc( { params: { ...params, site: locales }, preview } )
  }
}

export const getCraftIdFromSlug = ( {
  section,
  type = null,
  slugParam = 'slug',
} ) => (
  fetchData,
  useParams,
) => getFetchFunc( async ( { params, preview } ) => {
  const GET_ID_FROM_SLUG = `
  query(
    $site: [String!]
    $slug: [String!]
    $section: [String!]
    $type: [String]
  ) {
    entry(
      site: $site,
      slug: $slug,
      section: $section,
      type: $type,
    ) {
      sourceId
    }
  }
  `

  const { data } = await request(
    GET_ID_FROM_SLUG,
    {
      site: params.site,
      slug: params[ slugParam ],
      section,
      type,
    },
    preview,
  )

  const mergedParams = {
    ...params,
    id: data.entry?.sourceId,
    entryId: data.entry?.sourceId,
  }

  const r = getFetchFunc( fetchData, useParams )( { params: mergedParams, preview } )
  return r
}, useParams )

export const usePagination = (
  perPage = 10,
  fetchData,
  useParams,
) => getFetchFunc( async ( { params, preview } ) => {
  const { page = 1, ...otherParams } = params

  const mergedParams = {
    ...otherParams,
    offset: ( page - 1 ) * perPage,
    limit: perPage,
  }

  return getFetchFunc( fetchData, [
    ...useParams,
    'limit',
    'offset',
  ] )( {
    params: mergedParams,
    preview,
  } )
}, useParams )

export const assertCraftData = (
  assertServersideProps = props => Boolean( props?.entry ),
) => getServerSideProps => async ctx => {
  const result = await getServerSideProps( ctx )

  if ( !assertServersideProps( result?.props ) ) {
    ctx.res.statusCode = 404
    ctx.res.yada = 'yada'
  }

  return result
}
